<template>
    <div class="ro-table-wrapper">
        <el-table
            :data="orders"
            v-loading="loading"
            class="ro-table"
            empty-text="Chưa có yêu cầu tuyển dụng nào"
            stripe
            highlight-current-row
        >
        <!-- Mã / ID -->
        <el-table-column label="#" width="60" align="center">
            <template slot-scope="{ row }">
                <span class="ro-table__id">#{{ row.id }}</span>
            </template>
        </el-table-column>

        <!-- Tiêu đề & mô tả -->
        <el-table-column label="Tiêu đề yêu cầu" min-width="250">
            <template slot-scope="{ row }">
                <div class="ro-table__title" @click="$emit('view', row)">
                    <span v-if="row.code_order" class="ro-table__code-pill" :title="'Mã đơn: ' + row.code_order">
                        <i class="el-icon-tickets"></i> {{ row.code_order }}
                    </span>
                    <span>{{ row.title || row.position_title || "Yêu cầu tuyển dụng" }}</span>
                </div>
                <div v-if="row.description" class="ro-table__desc" :title="row.description">
                    {{ row.description }}
                </div>
                <div class="ro-table__meta-tags">
                    <span v-if="row.quantity || row.amount" class="ro-table__qty-tag">
                        <i class="el-icon-user"></i> <b>{{ row.quantity || row.amount }}</b> nhân sự
                    </span>
                    <span v-if="workTypeLabel(row)" class="ro-table__work-tag">
                        <i class="el-icon-suitcase"></i> {{ workTypeLabel(row) }}
                    </span>
                    <span v-if="row.branch_name || (row.branch && (row.branch.name || row.branch.branch_name)) || row.work_location || row.location" class="ro-table__loc-tag">
                        <i class="el-icon-location-outline"></i> {{ row.branch_name || (row.branch && (row.branch.name || row.branch.branch_name)) || row.work_location || row.location }}
                    </span>
                </div>
            </template>
        </el-table-column>

        <!-- Loại tuyển dụng -->
        <el-table-column label="Loại" width="160" align="center">
            <template slot-scope="{ row }">
                <order-type-tag :order="row" />
            </template>
        </el-table-column>

        <!-- Người tạo & Thời gian -->
        <el-table-column label="Người tạo" width="160">
            <template slot-scope="{ row }">
                <div class="ro-table__creator">
                    <i class="el-icon-user ro-icon-creator"></i>
                    <span class="ro-creator-name">{{ creatorName(row) }}</span>
                </div>
                <div class="ro-table__time">
                    <i class="el-icon-time ro-icon-time"></i>
                    <span>{{ formatDate(row.created_at) }}</span>
                </div>
            </template>
        </el-table-column>

        <!-- Phòng ban -->
        <el-table-column label="Phòng ban" width="175">
            <template slot-scope="{ row }">
                <div class="ro-table__dept" :title="departmentName(row)">
                    <i class="el-icon-office-building ro-icon-dept"></i>
                    <span class="ro-dept-name">{{ departmentName(row) }}</span>
                </div>
            </template>
        </el-table-column>

        <!-- Tiến độ duyệt HR & TL (chỉ hiển thị khi có department = 3 hoặc 52 và là Job tuyển hộ) -->
        <el-table-column v-if="showApprovalLevel" label="Duyệt cấp" width="145">
            <template slot-scope="{ row }">
                <div v-if="isTeacherOrder(row)" class="ro-table__approvals">
                    <div class="ro-table__approve-item">
                        <span class="ro-table__approve-role">HR</span>
                        <span
                            class="ro-table__approve-pill"
                            :class="approveStatusClass(row.hr_approve)"
                            :title="row.hr_reason_reject ? `Lý do từ chối: ${row.hr_reason_reject}` : ''"
                        >
                            <i :class="approveStatusIcon(row.hr_approve)"></i>
                            <span>{{ row.hr_approve_label || approveStatusText(row.hr_approve) }}</span>
                        </span>
                    </div>
                    <div class="ro-table__approve-item">
                        <span class="ro-table__approve-role">TL</span>
                        <span
                            class="ro-table__approve-pill"
                            :class="approveStatusClass(row.teamlead_approve)"
                            :title="row.team_lead_reason_reject ? `Lý do từ chối: ${row.team_lead_reason_reject}` : ''"
                        >
                            <i :class="approveStatusIcon(row.teamlead_approve)"></i>
                            <span>{{ row.teamlead_approve_label || approveStatusText(row.teamlead_approve) }}</span>
                        </span>
                    </div>
                </div>
                <span v-else class="ro-muted-dash">—</span>
            </template>
        </el-table-column>

        <!-- Người phụ trách (Lead/Người tạo) -->
        <el-table-column label="Người phụ trách" width="155">
            <template slot-scope="{ row }">
                <div v-if="leadInChargeName(row)" class="ro-table__lead" :title="leadInChargeName(row)">
                    <i class="el-icon-user ro-icon-lead"></i>
                    <span class="ro-table__lead-name">{{ leadInChargeName(row) }}</span>
                </div>
                <span v-else class="ro-muted-dash">—</span>
            </template>
        </el-table-column>

        <!-- Phụ trách & Điều phối -->
        <el-table-column label="Phụ trách / Điều phối" width="180">
            <template slot-scope="{ row }">
                <div
                    class="ro-table__pic"
                    :class="{ 'ro-table__pic--disabled': !canAssignPic(row) || !isApproved(row) }"
                    @click="handleAssignClick(row)"
                >
                    <span
                        v-if="hrInChargeName(row)"
                        class="ro-table__pic-active"
                        :title="canAssignPic(row) && isApproved(row) ? (isTeacherOrder(row) ? 'Bấm để đổi Trưởng nhóm phụ trách' : 'Bấm để đổi HR phụ trách') : ''"
                    >
                        <i class="el-icon-user-solid ro-icon-pic"></i>
                        <span class="ro-table__pic-name">{{ hrInChargeName(row) }}</span>
                        <i v-if="canAssignPic(row) && isApproved(row)" class="el-icon-edit ro-table__pic-edit-icon"></i>
                    </span>
                    <span
                        v-else-if="canAssignPic(row) && isApproved(row)"
                        class="ro-table__pic-empty"
                        :title="isTeacherOrder(row) ? 'Bấm để gán Trưởng nhóm phụ trách' : 'Bấm để gán HR phụ trách'"
                    >
                        <i class="el-icon-plus"></i>
                        <span>{{ isTeacherOrder(row) ? 'Gán trưởng nhóm' : 'Gán HR' }}</span>
                    </span>
                    <span
                        v-else
                        class="ro-table__pic-locked"
                        :title="!isApproved(row) ? 'Chỉ gán khi yêu cầu đã được duyệt' : 'Chưa gán người phụ trách'"
                    >
                        <i class="el-icon-lock"></i>
                        <span>Chưa gán</span>
                    </span>
                </div>

                <!-- Người điều phối (nếu có) -->
                <div
                    v-if="coordinatorName(row)"
                    class="ro-table__coordinator"
                    :title="`Người điều phối: ${coordinatorName(row)}`"
                >
                    <i class="el-icon-connection"></i>
                    <span>ĐP: {{ coordinatorName(row) }}</span>
                </div>

                <div
                    v-if="row.hiring_start_date"
                    class="ro-table__hiring-date"
                    :title="`Ngày bắt đầu chạy job: ${formatDay(row.hiring_start_date)}`"
                >
                    <i class="el-icon-date"></i>
                    <span>Chạy: {{ formatDay(row.hiring_start_date) }}</span>
                </div>
            </template>
        </el-table-column>

        <!-- Người duyệt CV (chỉ áp dụng cho job tuyển hộ) -->
        <el-table-column label="Người duyệt CV" width="165">
            <template slot-scope="{ row }">
                <div
                    v-if="isTeacherOrder(row)"
                    class="ro-table__pic"
                    :class="{ 'ro-table__pic--disabled': !canAssignCvApprover || isRejected(row) }"
                    @click="handleAssignCvApproverClick(row)"
                >
                    <span v-if="cvApproverName(row)" class="ro-table__pic-active" :title="canAssignCvApprover && !isRejected(row) ? 'Bấm để đổi người duyệt CV' : 'Người duyệt CV'">
                        <i class="el-icon-check ro-icon-approver"></i>
                        <span class="ro-table__pic-name">{{ cvApproverName(row) }}</span>
                        <i v-if="canAssignCvApprover && !isRejected(row)" class="el-icon-edit ro-table__pic-edit-icon"></i>
                    </span>
                    <span v-else-if="canAssignCvApprover && !isRejected(row)" class="ro-table__pic-empty" title="Bấm để gán người duyệt CV">
                        <i class="el-icon-plus"></i>
                        <span>Gán duyệt CV</span>
                    </span>
                    <span v-else class="ro-table__pic-locked" :title="isRejected(row) ? 'Yêu cầu tuyển dụng đã bị từ chối' : 'Chưa gán người duyệt CV'">
                        <i class="el-icon-lock"></i>
                        <span>{{ isRejected(row) ? 'Đã từ chối' : 'Chưa gán' }}</span>
                    </span>
                </div>
                <span v-else class="ro-muted-dash">—</span>
            </template>
        </el-table-column>

        <!-- Số ngày tuyển còn lại (Dành cho người có department_id = 3) -->
        <el-table-column
            v-if="showRemainingDays"
            label="Số ngày tuyển còn lại"
            width="170"
            align="center"
        >
            <template slot-scope="{ row }">
                <div class="ro-remaining-badge" :class="`ro-remaining-badge--${remainingDaysInfo(row).type}`">
                    <i :class="remainingDaysInfo(row).icon"></i>
                    <span>{{ remainingDaysInfo(row).text }}</span>
                </div>
            </template>
        </el-table-column>

        <!-- Trạng thái chung -->
        <el-table-column label="Trạng thái" width="160" align="center">
            <template slot-scope="{ row }">
                <order-status-tag :status="row.status" :status-label="row.status_label" />
            </template>
        </el-table-column>

        <!-- Thao tác -->
        <el-table-column label="Thao tác" width="195" fixed="right" align="center">
            <template slot-scope="{ row }">
                <div class="ro-table__actions" >
                    <!-- Nút Xem ứng viên của Order -->
                    <el-tooltip :content="!row.recruitment_job_id ? 'Order chưa được gán vào job' : `Xem danh sách ứng viên (${cvCount(row)})`" placement="top" :open-delay="200">
                        <el-button
                            :disabled="!row.recruitment_job_id"
                            size="mini"
                            class="ro-action-btn ro-action-btn--cvs"
                            @click="goToCvs(row)"
                        >
                            <i class="el-icon-document-copy"></i>
                        </el-button>
                    </el-tooltip>

                    <!-- Nút Nhập ngày bắt đầu chạy job (HR phụ trách nhập) -->
                    <el-tooltip :content="hiringStartDateTooltip(row)" placement="top" :open-delay="200">
                        <el-button
                            size="mini"
                            class="ro-action-btn ro-action-btn--hiring-date"
                            :class="{ 'ro-action-btn--hiring-date-active': !!row.hiring_start_date }"
                            @click="handleHiringStartDateClick(row)"
                        >
                            <i class="el-icon-date"></i>
                        </el-button>
                    </el-tooltip>

                    <!-- Chỉnh sửa -->
                    <el-tooltip content="Chỉnh sửa order" placement="top" :open-delay="200">
                        <el-button
                            size="mini"
                            class="ro-action-btn ro-action-btn--edit"
                            @click="$emit('edit', row)"
                        >
                            <i class="el-icon-edit"></i>
                        </el-button>
                    </el-tooltip>

                    <!-- Xóa -->
                    <el-tooltip v-if="canDelete(row)" content="Xoá yêu cầu" placement="top" :open-delay="200">
                        <el-button
                            size="mini"
                            class="ro-action-btn ro-action-btn--delete"
                            @click="$emit('delete', row)"
                        >
                            <i class="el-icon-delete"></i>
                        </el-button>
                    </el-tooltip>
                </div>
            </template>
        </el-table-column>
    </el-table>

    <!-- Dialog Nhập ngày bắt đầu chạy job (HR phụ trách nhập) -->
    <el-dialog
        :visible.sync="hiringDateDialogVisible"
        title="Ngày bắt đầu chạy job"
        width="480px"
        custom-class="ro-dialog ro-hiring-date-dialog"
        :close-on-click-modal="false"
        append-to-body
        @closed="onHiringDateDialogClosed"
    >
        <div v-if="selectedHiringOrder" class="ro-hiring-date-content">
            <div class="ro-hiring-order-info">
                <div class="ro-hiring-order-header">
                    <span class="ro-hiring-order-code">#{{ selectedHiringOrder.id }}</span>
                    <span class="ro-hiring-order-title">
                        {{ selectedHiringOrder.title || selectedHiringOrder.position_title || "Yêu cầu tuyển dụng" }}
                    </span>
                </div>
                <div class="ro-hiring-order-meta">
                    <span v-if="hrInChargeName(selectedHiringOrder)" class="ro-hiring-order-meta-item">
                        <i class="el-icon-user-solid"></i> HR phụ trách: <b>{{ hrInChargeName(selectedHiringOrder) }}</b>
                    </span>
                    <span v-if="selectedHiringOrder.created_at" class="ro-hiring-order-meta-item">
                        <i class="el-icon-time"></i> Tạo: {{ formatDate(selectedHiringOrder.created_at) }}
                    </span>
                </div>
            </div>

            <el-form label-position="top" size="small" class="ro-hiring-form">
                <el-form-item label="Ngày bắt đầu chạy job " required>
                    <el-date-picker
                        v-model="hiringStartDateValue"
                        type="date"
                        value-format="yyyy-MM-dd"
                        format="dd/MM/yyyy"
                        placeholder="Chọn ngày bắt đầu chạy job"
                        class="w-100"
                        clearable
                        :picker-options="hiringDatePickerOptions"
                    />
                    <div class="ro-hiring-date-help">
                        <i class="el-icon-info"></i>
                        <span>Ngày ghi nhận HR phụ trách chính thức bắt đầu triển khai tuyển dụng ứng viên cho yêu cầu này.</span>
                    </div>
                </el-form-item>
            </el-form>
        </div>

        <span slot="footer" class="dialog-footer">
            <el-button size="small" @click="hiringDateDialogVisible = false">Huỷ</el-button>
            <el-button
                size="small"
                type="primary"
                :loading="hiringDateSubmitting"
                icon="el-icon-check"
                @click="submitHiringStartDate"
            >
                Lưu ngày bắt đầu
            </el-button>
        </span>
    </el-dialog>
</div>
</template>

<script>
import moment from "moment-timezone";
import { mapGetters } from "vuex";
import {
    TYPE_TUYEN_HO,
    orderTypeOf,
    TYPE_APPROVE,
    CHO_DUYET,
    DA_DUYET,
    TU_CHOI,
    isOrderApproved,
    isOrderRejected,
    STATUS_KET_THUC,
    COORDINATOR_USER_ID,
    COORDINATOR_NAME,
} from "@/core/config/recruitmentOrders/recruitmentOrdersOption";
import {
    RO_UPDATE_ORDER,
    RO_UPDATE_HIRING_START_DATE,
} from "@/core/services/store/recruitment-orders/recruitmentOrders.module";
import OrderStatusTag from "./OrderStatusTag.vue";
import OrderTypeTag from "./OrderTypeTag.vue";

export default {
    name: "OrderTable",
    components: { OrderStatusTag, OrderTypeTag },
    props: {
        orders: { type: Array, default: () => [] },
        loading: { type: Boolean, default: false },
        currentUserId: { type: [Number, String], default: null },
        currentUser: { type: Object, default: null },
        isCoordinator: { type: Boolean, default: false },
        canAssignCvApprover: { type: Boolean, default: false },
        /** Chỉ account_type_id = 16 của department_id = 3 mới được gán HR phụ trách */
        canAssignPersonInCharge: { type: Boolean, default: false },
        /** Thuộc phòng Nhân sự (department_id = 3) -> hiển thị cột Số ngày tuyển còn lại */
        showRemainingDays: { type: Boolean, default: false },
        /** Thuộc phòng Nhân sự (department_id = 3) hoặc phòng TCGV (department_id = 52) -> hiển thị Duyệt cấp */
        showApprovalLevel: { type: Boolean, default: true },
    },
    computed: {
        ...mapGetters(["roWorkForms"]),
        workFormsMap() {
            const map = {};
            (this.roWorkForms || []).forEach((w) => {
                if (w && w.id !== undefined && w.id !== null) {
                    map[w.id] = w;
                }
            });
            return map;
        },
    },
    data() {
        return {
            hiringDateDialogVisible: false,
            selectedHiringOrder: null,
            hiringStartDateValue: "",
            hiringDateSubmitting: false,
            hiringDatePickerOptions: {
                shortcuts: [
                    {
                        text: "Hôm nay",
                        onClick(picker) {
                            picker.$emit("pick", new Date());
                        },
                    },
                    {
                        text: "Hôm qua",
                        onClick(picker) {
                            const date = new Date();
                            date.setTime(date.getTime() - 3600 * 1000 * 24);
                            picker.$emit("pick", date);
                        },
                    },
                    {
                        text: "Ngày mai",
                        onClick(picker) {
                            const date = new Date();
                            date.setTime(date.getTime() + 3600 * 1000 * 24);
                            picker.$emit("pick", date);
                        },
                    },
                ],
            },
        };
    },
    methods: {
        isApproved(row) {
            return isOrderApproved(row);
        },
        isRejected(row) {
            return isOrderRejected(row);
        },
        canAssignPic(row) {
            if (!row) return false;
            if (this.canAssignPersonInCharge) return true;
            if (this.isCoordinator) return true;
            if (row.coordinator_id && String(row.coordinator_id) === String(this.currentUserId)) return true;
            return false;
        },
        coordinatorName(row) {
            if (!row) return "";
            if (row.coordinator && row.coordinator.name) return row.coordinator.name;
            if (row.coordinator_name) return row.coordinator_name;
            if (row.coordinator_id) {
                if (String(row.coordinator_id) === String(COORDINATOR_USER_ID)) return COORDINATOR_NAME || "Chị Cẩm Tú";
                return `ID #${row.coordinator_id}`;
            }
            return "";
        },
        handleAssignClick(row) {
            if (!this.canAssignPic(row)) {
                this.$message.warning("Bạn không có quyền phân công người phụ trách cho yêu cầu này");
                return;
            }
            if (!this.isApproved(row)) {
                this.$message.warning("Chỉ yêu cầu tuyển dụng ở trạng thái Đã duyệt mới được gán người phụ trách");
                return;
            }
            this.$emit("assign", row);
        },
        handleAssignCvApproverClick(row) {
            if (!this.isTeacherOrder(row)) {
                this.$message.warning("Chỉ yêu cầu tuyển hộ mới được gán người duyệt CV");
                return;
            }
            if (!this.canAssignCvApprover) {
                this.$message.warning("Chỉ nhân sự thuộc Phòng Nhân sự mới được gán người duyệt CV");
                return;
            }
            if (this.isRejected(row)) {
                this.$message.warning("Yêu cầu tuyển dụng đã bị từ chối, không thể gán người duyệt CV");
                return;
            }
            this.$emit("assign-cv-approver", row);
        },
        isTeacherOrder(row) {
            return orderTypeOf(row) === TYPE_TUYEN_HO;
        },
        creatorName(row) {
            if (row.created_user_info && row.created_user_info.name) return row.created_user_info.name;
            if (row.created_by_name) return row.created_by_name;
            if (row.created_user) return `ID ${row.created_user}`;
            return "—";
        },
        departmentName(row) {
            if (row.department && row.department.name) return row.department.name;
            if (row.department_name) return row.department_name;
            return "—";
        },
        leadInChargeName(row) {
            if (!row) return "";
            if (row.teamlead && row.teamlead.name) return row.teamlead.name;
            if (row.teamlead_name) return row.teamlead_name;
            if (row.created_user_info && row.created_user_info.name) return row.created_user_info.name;
            if (row.created_by_name) return row.created_by_name;
            if (row.created_user) return `ID ${row.created_user}`;
            return "";
        },
        hrInChargeName(row) {
            if (!row) return "";
            if (row.person_in_charge_id == null && !row.person_in_charge) return "";
            if (row.person_in_charge && row.person_in_charge.name) {
                if (!row.person_in_charge_id || String(row.person_in_charge.id) === String(row.person_in_charge_id)) {
                    return row.person_in_charge.name;
                }
            }
            if (row.person_in_charge_name) return row.person_in_charge_name;
            if (row.assigned_hr_teamlead_name) return row.assigned_hr_teamlead_name;
            if (row.person_in_charge_id) return `ID #${row.person_in_charge_id}`;
            return "";
        },
        personInChargeName(row) {
            return this.hrInChargeName(row) || this.leadInChargeName(row) || "";
        },
        cvApproverName(row) {
            if (!row) return "";
            if (row.cv_approver_id == null && !row.cv_approver) return "";
            if (row.cv_approver && row.cv_approver.name) {
                if (!row.cv_approver_id || String(row.cv_approver.id) === String(row.cv_approver_id)) {
                    return row.cv_approver.name;
                }
            }
            if (row.cv_approver_name) return row.cv_approver_name;
            if (row.cv_approver_id) {
                if (String(row.cv_approver_id) === String(COORDINATOR_USER_ID)) {
                    return "Ms. Cẩm Tú";
                }
                return `ID #${row.cv_approver_id}`;
            }
            return "";
        },
        approveStatusText(val) {
            return TYPE_APPROVE[val] || "Chờ duyệt";
        },
        approveStatusClass(val) {
            const num = Number(val);
            if (num === DA_DUYET) return "ro-table__badge--success";
            if (num === TU_CHOI) return "ro-table__badge--danger";
            return "ro-table__badge--warning";
        },
        approveStatusIcon(val) {
            const num = Number(val);
            if (num === DA_DUYET) return "el-icon-circle-check";
            if (num === TU_CHOI) return "el-icon-circle-close";
            return "el-icon-time";
        },
        canDelete(row) {
            const isCreator = String(row.created_user || row.created_by) === String(this.currentUserId);
            return isCreator;
        },
        cvCount(row) {
            console.log(row, 'rowww');
            
            return (row && Array.isArray(row.cvs)) ? row.cvs.length : 0;
        },
        goToCvs(row) {
            if (!row || !row.id) return;
            this.$emit("view-cvs", row);
            const rJobId = (row.recruitment_job_id !== undefined && row.recruitment_job_id !== null && row.recruitment_job_id !== "")
                ? row.recruitment_job_id
                : row.id;
            this.$router.push({
                path: "/recruitment-cvs",
                query: {
                    job_id: String(rJobId),
                    recruitment_job_id: String(rJobId),
                    order_id: String(row.id),
                },
            }).catch(() => {});
        },
        handleTeamleadCommand(status, row) {
            this.$emit("teamlead-approve", { order: row, status });
        },
        workTypeLabel(row) {
            if (!row) return "";
            if (row.work_form && row.work_form.name) return row.work_form.name;
            if (row.work_type_name) return row.work_type_name;
            if (row.working_form_name) return row.working_form_name;
            const val = row.work_type !== undefined && row.work_type !== null && row.work_type !== ""
                ? row.work_type
                : (row.working_form !== undefined && row.working_form !== null && row.working_form !== "" ? row.working_form : null);
            if (val === null || val === undefined || val === "") return "";
            if (this.workFormsMap && this.workFormsMap[val]) {
                return this.workFormsMap[val].name;
            }
            if (val === 0 || val === "0" || String(val).toLowerCase() === "online") return "Online";
            if (val === 1 || val === "1" || String(val).toLowerCase() === "offline") return "Offline";
            return String(val);
        },
        remainingDaysInfo(row) {
            const end = row.end_date || row.end_time || row.deadline;
            if (!end) {
                return {
                    text: "Không giới hạn",
                    type: "normal",
                    icon: "el-icon-date",
                };
            }
            const endMoment = moment(end).isValid() ? moment(end).startOf("day") : moment(end, "YYYY-MM-DD").startOf("day");
            if (!endMoment.isValid()) {
                return {
                    text: "Không giới hạn",
                    type: "normal",
                    icon: "el-icon-date",
                };
            }
            const today = moment().startOf("day");
            const diffDays = endMoment.diff(today, "days");

            if (diffDays > 3) {
                return {
                    text: `Còn ${diffDays} ngày`,
                    type: "safe",
                    icon: "el-icon-time",
                };
            } else if (diffDays >= 1) {
                return {
                    text: `Còn ${diffDays} ngày`,
                    type: "warning",
                    icon: "el-icon-warning-outline",
                };
            } else if (diffDays === 0) {
                return {
                    text: "Hôm nay hết hạn",
                    type: "danger",
                    icon: "el-icon-alarm-clock",
                };
            } else {
                if (Number(row && row.status) === STATUS_KET_THUC) {
                    return {
                        text: "Đã kết thúc",
                        type: "normal",
                        icon: "el-icon-circle-check",
                    };
                }
                if (isOrderRejected(row)) {
                    return {
                        text: "Đã từ chối",
                        type: "normal",
                        icon: "el-icon-circle-close",
                    };
                }
                const past = Math.abs(diffDays);
                return {
                    text: `Quá hạn ${past} ngày`,
                    type: "danger",
                    icon: "el-icon-circle-close",
                };
            }
        },
        formatDate(v) {
            return v ? moment(v).format("DD/MM/YYYY HH:mm") : "";
        },
        formatDay(v) {
            return v ? moment(v).format("DD/MM/YYYY") : "";
        },
        hrInChargeId(row) {
            if (!row) return null;
            if (row.person_in_charge_id !== undefined && row.person_in_charge_id !== null && row.person_in_charge_id !== "") {
                return row.person_in_charge_id;
            }
            if (row.person_in_charge && row.person_in_charge.id) {
                return row.person_in_charge.id;
            }
            return null;
        },
        isHrInCharge(row) {
            if (!row || !this.currentUserId) return false;
            const picId = this.hrInChargeId(row);
            return picId !== null && String(picId) === String(this.currentUserId);
        },
        canEditHiringStartDate(row) {
            if (!row) return false;
            return this.isHrInCharge(row) || this.canAssignPersonInCharge;
        },
        hiringStartDateTooltip(row) {
            const formattedDate = row && row.hiring_start_date ? this.formatDay(row.hiring_start_date) : "";
            if (this.canEditHiringStartDate(row)) {
                return formattedDate
                    ? `Ngày bắt đầu chạy job: ${formattedDate} (Bấm để thay đổi)`
                    : "Nhập ngày bắt đầu chạy job";
            }
            return formattedDate
                ? `Ngày bắt đầu chạy job: ${formattedDate} (Chỉ HR phụ trách mới được đổi)`
                : "Nhập ngày bắt đầu chạy job (Chỉ HR phụ trách)";
        },
        handleHiringStartDateClick(row) {
            if (!this.canEditHiringStartDate(row)) {
                this.$message.warning("Chỉ HR phụ trách yêu cầu này mới được nhập ngày bắt đầu chạy job");
                return;
            }
            if (!this.isApproved(row)) {
                this.$message.warning("Chỉ yêu cầu tuyển dụng ở trạng thái Đã duyệt mới được nhập ngày bắt đầu chạy job");
                return;
            }
            this.selectedHiringOrder = row;
            this.hiringStartDateValue = row.hiring_start_date
                ? moment(row.hiring_start_date).format("YYYY-MM-DD")
                : moment().format("YYYY-MM-DD");
            this.hiringDateDialogVisible = true;
        },
        onHiringDateDialogClosed() {
            this.selectedHiringOrder = null;
            this.hiringStartDateValue = "";
        },
        submitHiringStartDate() {
            if (!this.selectedHiringOrder) return;
            if (!this.hiringStartDateValue) {
                this.$message.warning("Vui lòng chọn ngày bắt đầu chạy job");
                return;
            }

            this.hiringDateSubmitting = true;
            const orderId = this.selectedHiringOrder.id;
            const newDate = this.hiringStartDateValue;

            this.$store
                .dispatch(RO_UPDATE_HIRING_START_DATE, {
                    id: orderId,
                    hiring_start_date: newDate,
                })
                .then(() => {
                    this.$message.success("Cập nhật ngày bắt đầu chạy job thành công");
                    if (this.selectedHiringOrder) {
                        this.$set(this.selectedHiringOrder, "hiring_start_date", newDate);
                    }
                    this.hiringDateDialogVisible = false;
                    this.$emit("hiring-start-date-saved", {
                        order: this.selectedHiringOrder,
                        hiring_start_date: newDate,
                    });
                })
                .catch((err) => {
                    // Fallback thử RO_UPDATE_ORDER
                    this.$store
                        .dispatch(RO_UPDATE_ORDER, {
                            id: orderId,
                            hiring_start_date: newDate,
                        })
                        .then(() => {
                            this.$message.success("Cập nhật ngày bắt đầu chạy job thành công");
                            if (this.selectedHiringOrder) {
                                this.$set(this.selectedHiringOrder, "hiring_start_date", newDate);
                            }
                            this.hiringDateDialogVisible = false;
                            this.$emit("hiring-start-date-saved", {
                                order: this.selectedHiringOrder,
                                hiring_start_date: newDate,
                            });
                        })
                        .catch((fallbackErr) => {
                            const errorObj = fallbackErr || err;
                            const msg =
                                (errorObj && errorObj.response && errorObj.response.data && (errorObj.response.data.message || errorObj.response.data.error)) ||
                                (errorObj && errorObj.message) ||
                                "Cập nhật ngày bắt đầu chạy job thất bại";
                            this.$message.error(msg);
                        });
                })
                .finally(() => {
                    this.hiringDateSubmitting = false;
                });
        },
    },
};
</script>

<style scoped>
.ro-table {
    width: 100%;
    border-radius: 8px;
}
.ro-table /deep/ .el-table__header th {
    background-color: #f8fafc !important;
    color: #475569 !important;
    font-weight: 700 !important;
    font-size: 12px !important;
    text-transform: uppercase !important;
    letter-spacing: 0.03em !important;
    padding: 12px 0 !important;
    border-bottom: 1.5px solid #e2e8f0 !important;
}
.ro-table /deep/ .el-table__row td {
    padding: 12px 0 !important;
    vertical-align: middle !important;
}
.ro-table /deep/ .el-table__row:hover > td {
    background-color: #f8fafc !important;
}
.ro-table__id {
    font-weight: 700;
    color: #4f46e5;
    font-size: 12.5px;
    background: #eef2ff;
    padding: 3px 7px;
    border-radius: 6px;
    display: inline-block;
}
.ro-table__title {
    font-weight: 700;
    color: #0f172a;
    font-size: 13.5px;
    cursor: pointer;
    line-height: 1.4;
    transition: color 0.15s ease;
}
.ro-table__title:hover {
    color: #4f46e5;
    text-decoration: underline;
}
.ro-table__code-pill {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 11px;
    font-weight: 700;
    color: #b45309;
    background: #fef3c7;
    border: 1px solid #fde68a;
    padding: 1px 7px;
    border-radius: 4px;
    margin-right: 6px;
    vertical-align: middle;
    letter-spacing: 0.02em;
}
.ro-table__desc {
    font-size: 12px;
    color: #64748b;
    margin-top: 4px;
    display: -webkit-box;
    -webkit-line-clamp: 1;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    line-height: 1.4;
}
.ro-table__meta-tags {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;
    margin-top: 6px;
}
.ro-table__qty-tag {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 11px;
    color: #0284c7;
    background: #f0f9ff;
    border: 1px solid #bae6fd;
    padding: 2px 8px;
    border-radius: 999px;
    font-weight: 500;
}
.ro-table__qty-tag b {
    color: #0369a1;
    font-weight: 700;
}
.ro-table__work-tag {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 11px;
    color: #4338ca;
    background: #eef2ff;
    border: 1px solid #c7d2fe;
    padding: 2px 8px;
    border-radius: 999px;
    font-weight: 500;
}
.ro-table__loc-tag {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 11px;
    color: #047857;
    background: #ecfdf5;
    border: 1px solid #a7f3d0;
    padding: 2px 8px;
    border-radius: 999px;
    font-weight: 500;
}
.ro-table__creator {
    display: flex;
    align-items: center;
    gap: 6px;
    font-weight: 600;
    color: #1e293b;
    font-size: 13px;
    line-height: 1.3;
}
.ro-icon-creator {
    color: #6366f1;
    font-size: 13px;
    flex-shrink: 0;
}
.ro-creator-name {
    word-break: break-word;
}
.ro-table__time {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 11.5px;
    color: #94a3b8;
    margin-top: 4px;
}
.ro-icon-time {
    color: #94a3b8;
    font-size: 11.5px;
    flex-shrink: 0;
}
.ro-table__dept {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12.5px;
    color: #334155;
    font-weight: 500;
    line-height: 1.35;
}
.ro-icon-dept {
    color: #8b5cf6;
    font-size: 13px;
    flex-shrink: 0;
}
.ro-dept-name {
    word-break: break-word;
}
.ro-table__approvals {
    display: flex;
    flex-direction: column;
    gap: 5px;
}
.ro-table__approve-item {
    display: flex;
    align-items: center;
    gap: 6px;
}
.ro-table__approve-role {
    font-weight: 700;
    color: #64748b;
    font-size: 11px;
    width: 20px;
    flex-shrink: 0;
}
.ro-table__approve-pill {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 11.5px;
    font-weight: 600;
    padding: 2px 8px;
    border-radius: 6px;
    line-height: 1.3;
    white-space: nowrap;
}
.ro-table__badge--success {
    background: #ecfdf5;
    color: #059669;
    border: 1px solid #a7f3d0;
}
.ro-table__badge--danger {
    background: #fef2f2;
    color: #dc2626;
    border: 1px solid #fecaca;
}
.ro-table__badge--warning {
    background: #fffbeb;
    color: #d97706;
    border: 1px solid #fde68a;
}
.ro-table__lead {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    color: #1e293b;
    font-weight: 500;
}
.ro-icon-lead {
    color: #4f46e5;
    font-size: 13px;
    flex-shrink: 0;
}
.ro-table__lead-name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 125px;
}
.ro-table__pic-name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 105px;
}

.ro-table__pic {
    cursor: pointer;
    display: inline-flex;
    align-items: center;
}
.ro-table__pic-active {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 12.5px;
    font-weight: 600;
    color: #1e293b;
    padding: 4px 9px;
    border-radius: 7px;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    transition: all 0.15s ease;
}
.ro-table__pic-active:hover {
    background: #eef2ff;
    color: #4f46e5;
    border-color: #c7d2fe;
}
.ro-icon-pic {
    color: #4f46e5;
    font-size: 12.5px;
}
.ro-icon-approver {
    color: #059669;
    font-size: 12.5px;
}
.ro-table__pic-edit-icon {
    font-size: 11px;
    color: #94a3b8;
    margin-left: 2px;
    transition: color 0.15s ease;
}
.ro-table__pic-active:hover .ro-table__pic-edit-icon {
    color: #4f46e5;
}
.ro-table__pic-empty {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 12px;
    color: #4f46e5;
    background: #eef2ff;
    border: 1px dashed #a5b4fc;
    padding: 3px 9px;
    border-radius: 7px;
    font-weight: 600;
    transition: all 0.15s ease;
}
.ro-table__pic-empty:hover {
    background: #e0e7ff;
    color: #3730a3;
    border-style: solid;
}
.ro-table__coordinator {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 11px;
    color: #4f46e5;
    background: #eef2ff;
    padding: 1px 6px;
    border-radius: 4px;
    margin-top: 3px;
    font-weight: 600;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.ro-table__pic-locked {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    font-size: 11.5px;
    color: #94a3b8;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    padding: 3px 9px;
    border-radius: 7px;
    cursor: not-allowed;
}
.ro-table__pic--disabled {
    cursor: not-allowed;
}
.ro-table__actions {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    white-space: nowrap;
}
.ro-action-btn {
    width: 30px !important;
    height: 30px !important;
    border-radius: 7px !important;
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
    font-size: 13px !important;
    padding: 0 !important;
    margin: 0 !important;
    transition: all 0.15s ease !important;
    border: 1px solid transparent !important;
}
.ro-action-btn i {
    font-size: 13px !important;
}
.ro-action-btn:hover {
    transform: translateY(-2px);
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
}
.ro-action-btn--cvs {
    background: #eef2ff !important;
    color: #4f46e5 !important;
    border-color: #c7d2fe !important;
}
.ro-action-btn--cvs:hover {
    background: #4f46e5 !important;
    color: #ffffff !important;
    border-color: #4f46e5 !important;
}
.ro-action-btn--assign {
    background: #fffbeb !important;
    color: #d97706 !important;
    border-color: #fde68a !important;
}
.ro-action-btn--assign:hover {
    background: #d97706 !important;
    color: #ffffff !important;
    border-color: #d97706 !important;
}
.ro-action-btn--approver {
    background: #ecfdf5 !important;
    color: #059669 !important;
    border-color: #a7f3d0 !important;
}
.ro-action-btn--approver:hover {
    background: #059669 !important;
    color: #ffffff !important;
    border-color: #059669 !important;
}
.ro-action-btn--edit {
    background: #f1f5f9 !important;
    color: #475569 !important;
    border-color: #cbd5e1 !important;
}
.ro-action-btn--edit:hover {
    background: #475569 !important;
    color: #ffffff !important;
    border-color: #475569 !important;
}
.ro-action-btn--delete {
    background: #fef2f2 !important;
    color: #dc2626 !important;
    border-color: #fecaca !important;
}
.ro-action-btn--delete:hover {
    background: #dc2626 !important;
    color: #ffffff !important;
    border-color: #dc2626 !important;
}
.ro-btn-wrap {
    display: inline-flex;
}
.ro-remaining-badge {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    font-size: 11.5px;
    font-weight: 600;
    padding: 3px 9px;
    border-radius: 999px;
    line-height: 1.3;
    white-space: nowrap;
}
.ro-remaining-badge i {
    font-size: 12px;
}
.ro-remaining-badge--safe {
    background: #ecfdf5;
    color: #059669;
    border: 1px solid #a7f3d0;
}
.ro-remaining-badge--warning {
    background: #fffbeb;
    color: #d97706;
    border: 1px solid #fde68a;
}
.ro-remaining-badge--danger {
    background: #fef2f2;
    color: #dc2626;
    border: 1px solid #fecaca;
}
.ro-remaining-badge--normal {
    background: #f8fafc;
    color: #64748b;
    border: 1px solid #e2e8f0;
}

.ro-table-wrapper {
    width: 100%;
}

.ro-action-btn--hiring-date {
    background: #ecfeff !important;
    color: #0891b2 !important;
    border-color: #a5f3fc !important;
}
.ro-action-btn--hiring-date:hover {
    background: #0891b2 !important;
    color: #ffffff !important;
    border-color: #0891b2 !important;
}
.ro-action-btn--hiring-date-active {
    background: #0891b2 !important;
    color: #ffffff !important;
    border-color: #0891b2 !important;
}
.ro-action-btn--hiring-date-active:hover {
    background: #0e7490 !important;
    border-color: #0e7490 !important;
}

.ro-table__hiring-date {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 11px;
    font-weight: 600;
    color: #0891b2;
    background: #ecfeff;
    border: 1px solid #cffafe;
    padding: 1px 6px;
    border-radius: 4px;
    margin-top: 4px;
    white-space: nowrap;
}
.ro-table__hiring-date i {
    font-size: 11px;
}

/* Dialog Nhập ngày bắt đầu chạy job */
.ro-hiring-date-content {
    display: flex;
    flex-direction: column;
    gap: 16px;
}
.ro-hiring-order-info {
    padding: 12px 14px;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
}
.ro-hiring-order-header {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 6px;
}
.ro-hiring-order-code {
    font-size: 12px;
    font-weight: 700;
    color: #4f46e5;
    background: #eef2ff;
    padding: 2px 6px;
    border-radius: 4px;
}
.ro-hiring-order-title {
    font-size: 13.5px;
    font-weight: 700;
    color: #1e293b;
    line-height: 1.35;
}
.ro-hiring-order-meta {
    display: flex;
    align-items: center;
    gap: 12px;
    font-size: 12px;
    color: #64748b;
    flex-wrap: wrap;
}
.ro-hiring-order-meta-item {
    display: inline-flex;
    align-items: center;
    gap: 4px;
}
.ro-hiring-date-help {
    font-size: 12px;
    color: #64748b;
    margin-top: 6px;
    line-height: 1.4;
    display: flex;
    align-items: flex-start;
    gap: 6px;
}
.ro-hiring-date-help i {
    color: #0891b2;
    margin-top: 2px;
    font-size: 13px;
}
</style>
